/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { Input } from "baseui/input";
import { Button } from "baseui/button";
import { HeadingXXLarge, LabelLarge } from "baseui/typography";
import { CenteredContent } from "./DesignSystem/Containers";
import { DexterAuthContext } from "./Utils/AuthContext";
import { useDexterTheme } from "./DesignSystem/Theme/ThemeContext";
import { notify } from "./Shared/Notify";
import { LOGO_URL } from "./Utils/SiteLogo.mjs";
import {
  MIN_PASSWORD_LENGTH,
  isValidEmail,
  isPasswordLongEnough,
} from "./Utils/AuthHelpers";
import { PLACEMENT, StatefulTooltip } from "baseui/tooltip";
import { getTooltipOverrides } from "./DesignSystem/TooltipStyles";
import { Hide, Show } from "baseui/icon";

export const RegistrationForm = ({
  onBackToLogin,
}: {
  onBackToLogin: () => void;
}): JSX.Element => {
  const { currentTheme } = useDexterTheme();
  const dexterAuth = React.useContext(DexterAuthContext);

  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Track which fields the user has left (blurred). Validation hints only show
  // after a field has been touched, so we don't nag while they're still typing.
  const [touched, setTouched] = React.useState({
    email: false,
    password: false,
    confirmPassword: false,
  });

  const markTouched = (field: "email" | "password" | "confirmPassword"): void =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const emailIsValid = isValidEmail(email);
  const passwordLongEnough = isPasswordLongEnough(password);
  const passwordsMatch =
    password === confirmPassword && confirmPassword.length > 0;
  const noEmptyFields =
    name.trim() !== "" &&
    email.trim() !== "" &&
    password !== "" &&
    confirmPassword !== "";

  const canSubmit =
    noEmptyFields && emailIsValid && passwordLongEnough && passwordsMatch;

  const handleRegister = async (): Promise<void> => {
    if (!canSubmit || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    const succeeded = await dexterAuth.registerUser(name, email, password);
    setIsSubmitting(false);

    if (succeeded) {
      notify.positive("Registration successful. You can now log in.");
      onBackToLogin();
    }
    // On failure, registerUser already surfaced the server error via
    // notifyGeneralError; stay on the form so the user can correct and retry.
  };

  const primaryButtonOverrides = {
    BaseButton: {
      style: ({ $disabled }: { $disabled: boolean }) => ({
        backgroundColor: $disabled
          ? currentTheme.colors.buttonDisabledColor
          : currentTheme.colors.primaryButtonColor,
        color: $disabled
          ? currentTheme.colors.secondaryTextColor
          : currentTheme.colors.primaryElementFontColor,
      }),
    },
  };

  const secondaryButtonOverrides = {
    BaseButton: {
      style: {
        backgroundColor: currentTheme.colors.backgroundTertiary,
        color: currentTheme.colors.text,
      },
    },
  };

  // Updated show/hide icons as custom components for tooltip consistency
  const MaskToggleShowIconNoTitle = React.forwardRef<
    SVGSVGElement,
    React.ComponentProps<typeof Show>
  >((props, ref) => {
    const { currentTheme } = useDexterTheme();
    return (
      <StatefulTooltip
        placement={PLACEMENT.right}
        content={"Show Password Text"}
        overrides={getTooltipOverrides(currentTheme)}
      >
        <Show {...props} ref={ref} title="" />
      </StatefulTooltip>
    );
  });

  MaskToggleShowIconNoTitle.displayName = "MaskToggleShowIconNoTitle";

  const MaskToggleHideIconNoTitle = React.forwardRef<
    SVGSVGElement,
    React.ComponentProps<typeof Hide>
  >((props, ref) => {
    const { currentTheme } = useDexterTheme();
    return (
      <StatefulTooltip
        placement={PLACEMENT.right}
        content={"Hide Password Text"}
        overrides={getTooltipOverrides(currentTheme)}
      >
        <Hide {...props} ref={ref} title="" />
      </StatefulTooltip>
    );
  });

  MaskToggleHideIconNoTitle.displayName = "MaskToggleHideIconNoTitle";

  const inputOverrides = {
    Root: {
      style: {
        backgroundColor: currentTheme.colors.dropdownBackground,
        borderTopColor: currentTheme.colors.dropdownBorderColor,
        borderBottomColor: currentTheme.colors.dropdownBorderColor,
        borderLeftColor: currentTheme.colors.dropdownBorderColor,
        borderRightColor: currentTheme.colors.dropdownBorderColor,
      },
    },
    Input: {
      style: {
        backgroundColor: currentTheme.colors.dropdownBackground,
        color: currentTheme.colors.dropdownText,
        caretColor: currentTheme.colors.dropdownText,
        "::placeholder": {
          color: currentTheme.colors.secondaryTextColor,
        },
      },
    },
    InputContainer: {
      style: {
        backgroundColor: currentTheme.colors.dropdownBackground,
      },
    },
    EndEnhancer: {
      style: {
        backgroundColor: currentTheme.colors.dropdownBackground,
      },
    },
    MaskToggleButton: {
      props: { title: null },
      style: ({ $isHovered }: { $isHovered: boolean }) => ({
        backgroundColor: $isHovered
          ? currentTheme.colors.dropdownItemHoverBackground
          : currentTheme.colors.dropdownBackground,
        color: currentTheme.colors.dropdownText,
      }),
    },
    MaskToggleShowIcon: MaskToggleShowIconNoTitle,
    MaskToggleHideIcon: MaskToggleHideIconNoTitle,
  };

  const showEmailHint =
    touched.email && email.trim().length > 0 && !emailIsValid;
  const showPasswordLengthHint =
    touched.password && password.length > 0 && !passwordLongEnough;
  const showMismatchHint =
    touched.confirmPassword && confirmPassword.length > 0 && !passwordsMatch;

  return (
    <CenteredContent>
      <img src={LOGO_URL.toString()} height="200rem" />
      <HeadingXXLarge>{"Create an account"}</HeadingXXLarge>
      <div style={{ width: "20rem" }}>
        <div style={{ paddingBottom: "1rem" }}>
          <Input
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            placeholder="Name"
            data-testid="register-name"
            overrides={inputOverrides}
          />
        </div>
        <div style={{ paddingBottom: "1rem" }}>
          <Input
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            onBlur={() => markTouched("email")}
            placeholder="Email"
            data-testid="register-email"
            overrides={inputOverrides}
          />
        </div>
        <div style={{ paddingBottom: "1rem" }}>
          <Input
            value={password}
            type="password"
            onChange={(e) => setPassword(e.currentTarget.value)}
            onBlur={() => markTouched("password")}
            placeholder={`Password (min ${MIN_PASSWORD_LENGTH} characters)`}
            data-testid="register-password"
            overrides={inputOverrides}
          />
        </div>
        <div style={{ paddingBottom: "1rem" }}>
          <Input
            value={confirmPassword}
            type="password"
            onChange={(e) => setConfirmPassword(e.currentTarget.value)}
            onBlur={() => markTouched("confirmPassword")}
            placeholder="Confirm password"
            data-testid="register-confirm-password"
            overrides={inputOverrides}
            onKeyDown={(e) => e.key === "Enter" && handleRegister()}
          />
        </div>
        <div style={{ minHeight: "1.5rem", paddingTop: "0.25rem" }}>
          {showEmailHint && (
            <LabelLarge color={currentTheme.colors.errorTextColor}>
              {"Enter a valid email address."}
            </LabelLarge>
          )}
          {showPasswordLengthHint && (
            <LabelLarge color={currentTheme.colors.errorTextColor}>
              {"Password must be at least 12 characters."}
            </LabelLarge>
          )}
          {showMismatchHint && (
            <LabelLarge color={currentTheme.colors.errorTextColor}>
              {"Passwords do not match."}
            </LabelLarge>
          )}
        </div>
      </div>
      <div style={{ display: "flex", gap: "1rem", paddingTop: "1rem" }}>
        <Button
          kind="secondary"
          data-testid="register-back-button"
          onClick={onBackToLogin}
          overrides={secondaryButtonOverrides}
        >
          Back
        </Button>
        <Button
          disabled={!canSubmit || isSubmitting}
          data-testid="register-submit-button"
          onClick={handleRegister}
          overrides={primaryButtonOverrides}
        >
          Register
        </Button>
      </div>
    </CenteredContent>
  );
};
